// router/AppRouter.js
import React, { lazy, Suspense, useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { ProtectedRoute } from "../components/ProtectedRoute/ProtectedRoute";
import { useAuthStore } from "~/store/authStore";
import { useChartsStore } from "~/store/chartStore";
import { extractAuthFromUrl, clearAuthFromUrl } from "~/utils/urlAuth";
import { ChartSelectionModal } from "~/components/ChartSelectionModal/ChartSelectionModalEnhanced";
import { isWebOnWindows } from "~/desktop/platform.js";
import { PollModal } from "~/components/PollModal/PollModal";
import usePollStore from "~/store/pollStore";
import { shouldShowWhatsNew, markWhatsNewAsSeen } from "~/components/WhatsNew";
import { getScopedItem } from "~/store/workspaceStore";
import { getChartPreferenceKey } from "~/workspace/constants";
import HourglassLoader from "~/components/HourglassLoader/HourglassLoader";

// Lazy load pages for better performance
import Main from "../pages/Main/main";
const Login = lazy(() => import("../pages/Login/Login"));
const Welcome = lazy(() => import("../pages/Welcome/Welcome"));
const AuthError = lazy(() => import("../pages/AuthError/AuthError"));
const IconSpriteGallery = lazy(() => import("../pages/Dev/IconSpriteGallery"));
const Oob = lazy(() => import("../pages/Oob/Oob"));

// Loading component — mesmo visual do splash estático do index.html (transição contínua no boot)
const PageLoader = () => <HourglassLoader />;

export const AppRouter = () => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const loginWithBrokerAuth = useAuthStore(
    (state) => state.loginWithBrokerAuth,
  );
  const login = useAuthStore((state) => state.login);
  const initializeCharts = useChartsStore((state) => state.initialize);

  const [authChecked, setAuthChecked] = useState(false);
  const [autoLoginAttempted, setAutoLoginAttempted] = useState(false);
  const [tokenAuthFailed, setTokenAuthFailed] = useState(false);
  const [hasAuthToken, setHasAuthToken] = useState(false);

  // Modal sequence state: Poll → WhatsNew → ChartSelector
  const [showPoll, setShowPoll] = useState(false);
  const [pollCompleted, setPollCompleted] = useState(false);
  const [whatsNewCompleted, setWhatsNewCompleted] = useState(false);
  const [needsChartSelector, setNeedsChartSelector] = useState(false);
  const pollLoadFromServer = usePollStore((s) => s.loadFromServer);
  const pollShouldShow = usePollStore((s) => s.shouldShow);

  // Chart Selection Modal state
  const [showChartSelection, setShowChartSelection] = useState(false);
  const [originalAuthToken, setOriginalAuthToken] = useState(null);

  // Check for URL auth token or force-reload auth on mount
  useEffect(() => {
    const checkUrlAuth = async () => {
      // Only attempt auto-login once
      if (autoLoginAttempted) {
        setAuthChecked(true);
        return;
      }

      // STEP 0: Check if user is accessing the login page directly
      // If so, clear any cached auth to force manual login
      if (
        window.location.pathname === "/a7f3e9d2-4b1c-4a8f-9e3d-7c2b5a8f1e4d"
      ) {
        console.log("[AppRouter] Login page accessed - clearing cached auth");
        localStorage.removeItem("force-reload-auth");
        localStorage.removeItem("force-reload-original-token");
        setAuthChecked(true);
        setAutoLoginAttempted(true);
        return;
      }

      // STEP 1: Check for URL auth token FIRST (highest priority - explicit user intent)
      const urlAuth = extractAuthFromUrl();

      if (urlAuth) {
        // URL auth takes precedence - clear any cached auth to prevent conflicts
        localStorage.removeItem("force-reload-auth");
        localStorage.removeItem("force-reload-original-token");

        setHasAuthToken(true);
        console.log(
          `[AppRouter] Auth token detected in URL, broker: ${urlAuth.broker}`,
        );
        setAutoLoginAttempted(true);

        try {
          const success = await loginWithBrokerAuth(
            urlAuth.broker,
            urlAuth.authToken,
          );

          if (success) {
            console.log(
              `[AppRouter] Auto-login successful, clearing auth from URL`,
            );
            clearAuthFromUrl();
            setTokenAuthFailed(false);
          } else {
            console.error(`[AppRouter] Auto-login failed - showing error page`);
            setTokenAuthFailed(true);
          }
        } catch (error) {
          console.error(`[AppRouter] Auto-login error:`, error);
          setTokenAuthFailed(true);
        }

        setAuthChecked(true);
        return;
      }

      // STEP 2: Check if this is a force reload with preserved auth (fallback only)
      const forceReloadAuthStr = localStorage.getItem("force-reload-auth");
      if (forceReloadAuthStr) {
        console.log(
          "[AppRouter] ========== FORCE RELOAD AUTH DETECTED ==========",
        );
        try {
          const forceReloadAuth = JSON.parse(forceReloadAuthStr);
          const { broker, auth, loginMethod, timestamp } = forceReloadAuth;

          // Check if auth data is not too old (max 5 minutes)
          const age = Date.now() - timestamp;
          const maxAge = 5 * 60 * 1000; // 5 minutes

          if (age > maxAge) {
            console.warn(
              "[AppRouter] Force reload auth data too old, ignoring",
            );
            localStorage.removeItem("force-reload-auth");
            localStorage.removeItem("force-reload-original-token");
          } else {
            console.log("[AppRouter] Restoring auth from force reload:", {
              broker,
              loginMethod,
            });
            setAutoLoginAttempted(true);

            // Restore original token for brokerAuth users (for chart selection modal)
            if (loginMethod === "brokerAuth") {
              const originalToken = localStorage.getItem(
                "force-reload-original-token",
              );
              if (originalToken) {
                sessionStorage.setItem("original-auth-token", originalToken);
                console.log(
                  "[AppRouter] Original auth token restored to sessionStorage",
                );
                localStorage.removeItem("force-reload-original-token");
              }
            }

            // Perform auth based on login method
            let success = false;
            if (loginMethod === "brokerAuth") {
              success = await loginWithBrokerAuth(broker, auth);
            } else {
              // For credentials login, we use the auth token that marketdata.startUsingCredentials returned
              // This is actually the JWT token response from the server
              success = await loginWithBrokerAuth(broker, auth);
            }

            if (success) {
              console.log(
                "[AppRouter] Force reload auth restoration successful",
              );
              setTokenAuthFailed(false);
            } else {
              console.error("[AppRouter] Force reload auth restoration failed");
              setTokenAuthFailed(true);
              // Clear cached auth on failure to prevent re-login with stale credentials
              localStorage.removeItem("force-reload-auth");
              localStorage.removeItem("force-reload-original-token");
            }

            // Clean up localStorage on success
            if (success) {
              localStorage.removeItem("force-reload-auth");
            }
            setAuthChecked(true);
            return;
          }
        } catch (error) {
          console.error("[AppRouter] Error parsing force-reload-auth:", error);
          localStorage.removeItem("force-reload-auth");
          localStorage.removeItem("force-reload-original-token");
        }
      }

      setAuthChecked(true);
    };

    checkUrlAuth();
  }, [loginWithBrokerAuth, login, autoLoginAttempted]);

  // Initialize charts when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      initializeCharts();
    }
  }, [isAuthenticated, initializeCharts]);

  // Show modal for broker auth flow only (not for manual login)
  useEffect(() => {
    // Only show modal if user authenticated via URL auth token (broker flow)
    // Skip modal for manual login form authentication
    console.log(
      "[AppRouter] ========== CHART SELECTION MODAL DEBUG ==========",
    );
    console.log("[AppRouter] Modal check conditions:", {
      isAuthenticated,
      tokenAuthFailed,
      autoLoginAttempted,
      hasAuthToken,
      currentPath: window.location.pathname,
      urlSearch: window.location.search,
    });

    // Don't show modal if user is on /Oob route (Silverlight page)
    if (window.location.pathname === "/Oob") {
      console.log("[AppRouter] ❌ User on /Oob route, skipping modal");
      console.log(
        "[AppRouter] ================================================",
      );
      return;
    }

    // Suppress via sessionStorage flag (set by login page when &noPopups=true)
    if (sessionStorage.getItem("noPopups") === "true") {
      console.log(
        "[AppRouter] ❌ noPopups=true, skipping chart selection modal",
      );
      console.log(
        "[AppRouter] ================================================",
      );
      return;
    }

    // Check for debug parameter to force modal
    const forceModal =
      new URLSearchParams(window.location.search).get("forceModal") === "true";
    console.log("[AppRouter] Force modal parameter:", forceModal);

    console.log(
      "[AppRouter] Main condition check: isAuthenticated && !tokenAuthFailed && (autoLoginAttempted || forceModal)",
    );
    console.log(
      "[AppRouter] Result:",
      isAuthenticated && !tokenAuthFailed && (autoLoginAttempted || forceModal),
    );

    if (
      isAuthenticated &&
      !tokenAuthFailed &&
      (autoLoginAttempted || forceModal)
    ) {
      console.log("[AppRouter] ✅ Passed main condition check");

      // Get original token from sessionStorage (stored during extractAuthFromUrl)
      // The URL has already been cleared by this point, so we can't extract it again
      // For manual login (forceModal), use a demo token
      const originalToken =
        sessionStorage.getItem("original-auth-token") ||
        (forceModal ? "demo-token-for-manual-login" : null);
      console.log(
        "[AppRouter] Original token from sessionStorage:",
        originalToken ? `${originalToken.substring(0, 20)}...` : "NOT FOUND",
      );

      if (originalToken) {
        setOriginalAuthToken(originalToken);
      }

      // Track timers for cleanup
      let contextCheckTimer;

      // Import workspace context getter
      import("~/store/workspaceStore").then(({ getWorkspaceContext }) => {
        // Wait 500ms for workspace context to be fully set before checking preference
        // This prevents a race condition where getScopedItem is called before
        // setWorkspaceContext completes in handleAuthSuccess
        console.log(
          "[AppRouter] ⏱️  Starting 500ms timer for workspace context check...",
        );
        contextCheckTimer = setTimeout(() => {
          console.log(
            "[AppRouter] 🔍 500ms timer fired, checking workspace context and preferences...",
          );

          // Log workspace context
          const context = getWorkspaceContext();
          console.log(
            "[AppRouter] Workspace context (from workspaceStore):",
            context,
          );

          // Check if user has already set their preference (user-scoped)
          const chartPreference = getScopedItem(getChartPreferenceKey);
          const fullKey = getChartPreferenceKey(context.user, context.broker);

          console.log("[AppRouter] Chart preference check:", {
            keyGenerator: "getChartPreferenceKey",
            currentUser: context.user,
            currentBroker: context.broker,
            generatedKey: fullKey,
            preferenceValue: chartPreference,
            shouldShowModal: chartPreference !== "tradezone-charts-26",
          });

          // Log all localStorage keys related to chart preference
          const allKeys = Object.keys(localStorage).filter((k) =>
            k.includes("chart-preference"),
          );
          console.log(
            "[AppRouter] All chart-preference keys in localStorage:",
            allKeys,
          );
          allKeys.forEach((k) => {
            console.log(`  - ${k}: ${localStorage.getItem(k)}`);
          });

          // Determine which modals are needed. The chart selector ("gráficos
          // antigos") is web-on-Windows only — never the native app, never
          // mac/mobile/tablet — so gate the auto-open on the platform too.
          const willNeedChartSelector =
            isWebOnWindows() && chartPreference !== "tradezone-charts-26";
          setNeedsChartSelector(willNeedChartSelector);

          if (willNeedChartSelector) {
            console.log(
              "[AppRouter] ℹ️ Chart selector will be needed — WhatsNew will be skipped",
            );
          } else {
            console.log(
              "[AppRouter] ℹ️ Chart preference already set, chart selector not needed",
            );
          }

          // Load poll data from server, then start sequence
          pollLoadFromServer().then(() => {
            if (pollShouldShow()) {
              console.log(
                "[AppRouter] 📊 Poll not yet voted, showing poll first",
              );
              setShowPoll(true);
            } else {
              console.log("[AppRouter] 📊 Poll already voted, skipping poll");
              setPollCompleted(true);
            }
          });
        }, 500);
      });

      return () => {
        clearTimeout(contextCheckTimer);
      };
    } else {
      console.log(
        "[AppRouter] ❌ Failed main condition check, modal will NOT show",
      );
      console.log("[AppRouter] Reason:");
      if (!isAuthenticated) console.log("[AppRouter]   - Not authenticated");
      if (tokenAuthFailed) console.log("[AppRouter]   - Token auth failed");
      if (!autoLoginAttempted)
        console.log("[AppRouter]   - Auto login not attempted (manual login?)");
      console.log(
        "[AppRouter] Tip: Add ?forceModal=true to URL to test modal with manual login",
      );
      console.log(
        "[AppRouter] ================================================",
      );
    }
  }, [isAuthenticated, tokenAuthFailed, autoLoginAttempted]);

  // Step 2: After poll → decide WhatsNew or ChartSelector
  useEffect(() => {
    if (!pollCompleted) return;

    if (needsChartSelector) {
      // ChartSelector needed → skip WhatsNew, go directly to ChartSelector
      // Mark WhatsNew as seen so it doesn't appear next time if user disables ChartSelector
      markWhatsNewAsSeen();
      console.log(
        "[AppRouter] ✅ Poll completed → skipping WhatsNew (chart selector takes priority)",
      );
      setWhatsNewCompleted(true);
    } else if (shouldShowWhatsNew()) {
      // No ChartSelector → show WhatsNew
      console.log("[AppRouter] ✅ Poll completed → showing WhatsNew");
      const timer = setTimeout(() => {
        window.dispatchEvent(new Event("whatsNew:open"));
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      console.log("[AppRouter] ℹ️ Poll completed, no WhatsNew needed");
      setWhatsNewCompleted(true);
    }
  }, [pollCompleted, needsChartSelector]);

  // Listen for WhatsNew close to advance sequence
  useEffect(() => {
    const handler = () => {
      console.log("[AppRouter] WhatsNew closed, advancing sequence");
      setWhatsNewCompleted(true);
    };
    window.addEventListener("whatsNew:closed", handler);
    return () => window.removeEventListener("whatsNew:closed", handler);
  }, []);

  // Step 3: After WhatsNew → open ChartSelector (if needed)
  useEffect(() => {
    if (!whatsNewCompleted) return;

    if (needsChartSelector) {
      console.log(
        "[AppRouter] ✅ Sequence complete, scheduling chart selection modal...",
      );
      const timer = setTimeout(() => {
        console.log("[AppRouter] 🎉 Opening chart selection modal now!");
        setShowChartSelection(true);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      console.log("[AppRouter] ℹ️ Sequence complete, no chart selector needed");
    }
  }, [whatsNewCompleted, needsChartSelector]);

  return (
    <Router>
      {/* Show loader while checking auth */}
      {!authChecked ? (
        <PageLoader />
      ) : tokenAuthFailed ? (
        // If token auth failed, show error page
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="*" element={<AuthError />} />
          </Routes>
        </Suspense>
      ) : (
        // Normal routing after auth check complete
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Hidden login route for internal users only */}
            <Route
              path="/a7f3e9d2-4b1c-4a8f-9e3d-7c2b5a8f1e4d"
              element={
                isAuthenticated ? <Navigate to="/" replace /> : <Login />
              }
            />
            {/* Old /login route redirects to landing page */}
            <Route path="/login" element={<Navigate to="/" replace />} />
            <Route path="/dev/icon-gallery" element={<IconSpriteGallery />} />

            {/* Legacy /Index path support for broker redirects */}
            <Route
              path="/Index"
              element={
                isAuthenticated ? (
                  <Main />
                ) : (
                  <Welcome showLoading={hasAuthToken} />
                )
              }
            />

            {/* /Oob route for Silverlight Out of Browser */}
            <Route path="/Oob" element={<Oob />} />

            <Route
              path="/"
              element={
                isAuthenticated ? (
                  <Main />
                ) : (
                  <Welcome showLoading={hasAuthToken} />
                )
              }
            />
          </Routes>
        </Suspense>
      )}

      {/* Poll Modal */}
      <PollModal
        visible={showPoll}
        onDismiss={() => {
          setShowPoll(false);
          setPollCompleted(true);
        }}
      />

      {/* Chart Selection Modal */}
      <ChartSelectionModal
        visible={showChartSelection}
        onDismiss={() => setShowChartSelection(false)}
        authToken={originalAuthToken || "demo-token"}
      />
    </Router>
  );
};
